import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  clampTimeToPlayable,
  getEffectiveDuration,
} from '@/lib/audio-duration';
import getProxiedAudioUrl from '@/lib/audio-url';
import { TCF_CONSENT_CHANGED_EVENT_TYPE } from '@/lib/constants';
import usePlayer from '@/lib/hooks/use-player';
import { addTCStringToUrlSync } from '@/lib/tcf-consent';
import { isSafari, scheduleAnimationFrame } from '@/lib/utils';
import { useStoreDispatch } from '@/store';
import {
  selectPlayerCurrentAudio,
  selectPlayerIsMuted,
  selectPlayerVolume,
  setCurrentAudio,
  setIsAudioPlaying,
} from '@/store/slices/player';
import { setCurrentPlayingVideo } from '@/store/slices/video';
import {
  ContentFeedEntrySoundpiece,
  PlaylistItem as PlaylistItemProps,
} from '@/types/views/generic';

export type AudioTimeUpdateEvent = {
  currentTime: number;
  duration: number;
  isPaused: boolean;
};

type UseAudioPlayer = {
  onPlay: (
    index: number,
    localEntry: ContentFeedEntrySoundpiece | PlaylistItemProps,
  ) => void;
  onPause: () => void;
  onStop: () => void;
  onResume: () => void;
  setIsAutoPlay: (isAutoPlay: boolean) => void;
  skipAudio: (step: number) => void;
  setCurrentTime: (newTime: number) => void;
  changeEvent?: AudioTimeUpdateEvent;
  isSeekable: boolean;
  addToAutoPlaylist: (
    entry: PlaylistItemProps | ContentFeedEntrySoundpiece,
    index?: number,
  ) => void;
};

type UseAudioPlayerProps = {
  autoPlaylist?: PlaylistItemProps[] | ContentFeedEntrySoundpiece[];
};

let audio: HTMLAudioElement;
let isAutoPlay = false;
let baseAudioUrlForTcf: string | null = null;
let currentAudioIndex = -1;
let fallbackDuration: number | null = null;
let isResetAfterEnd = false; // Track when audio was reset to zero after reaching the end

// --- main hook --------------------------------------------------------------

const useAudioPlayer = ({
  autoPlaylist,
}: UseAudioPlayerProps = {}): UseAudioPlayer => {
  const radioPlayer = usePlayer();
  const localAutoPlaylist =
    autoPlaylist ?? ([] as PlaylistItemProps[] | ContentFeedEntrySoundpiece[]);

  const isMuted = useSelector(selectPlayerIsMuted);
  const volume = useSelector(selectPlayerVolume);
  const currentAudio = useSelector(selectPlayerCurrentAudio);

  const dispatch = useStoreDispatch();
  const [changeEvent, setChangeEvent] = useState<AudioTimeUpdateEvent>();
  const [isSeekable, setIsSeekable] = useState<boolean>(false);

  const ensureAudio = () => {
    if (!audio) {
      audio = new Audio();
      audio.preload = 'metadata';
    }
  };

  const addToLocalAutoPlaylist = (
    entry: PlaylistItemProps | ContentFeedEntrySoundpiece,
    index?: number,
  ) => {
    if (index !== undefined && index >= 0) {
      localAutoPlaylist[index] = entry;
    } else {
      // @ts-ignore be either of the two
      localAutoPlaylist.push(entry);
    }
  };

  const setIsAutoPlay = (value: boolean) => {
    isAutoPlay = value;
  };

  const onAutoPlayNext = (index: number) => {
    const nextIndex = index + 1;
    const hasNextSoundpiece =
      isAutoPlay && localAutoPlaylist && nextIndex < localAutoPlaylist.length;

    if (!hasNextSoundpiece) {
      dispatch(setIsAudioPlaying(false));

      return;
    }

    onPlay(nextIndex, localAutoPlaylist[nextIndex]);
  };

  const getIsSeekable = (): boolean => {
    if (!audio) return false;

    const { duration, seekable } = audio;
    const hasRanges = seekable.length > 0;

    if (hasRanges) {
      const start = seekable.start(0);
      const end = seekable.end(seekable.length - 1);

      if (end === 0 && start === 0 && (!isFinite(duration) || duration === 0)) {
        return false;
      }

      return true;
    }

    // no ranges, no duration → probably live stream
    return isFinite(duration) && duration > 0;
  };

  const updateChangeEvent = () => {
    if (!audio) return;
    const { currentTime, duration, paused } = audio;

    if (!isFinite(currentTime) || currentTime < 0) return;

    const effectiveDuration = getEffectiveDuration(audio, fallbackDuration);
    const clampedTime = clampTimeToPlayable(
      currentTime,
      audio,
      fallbackDuration,
    );
    const displayDuration =
      isFinite(duration) && duration > 0
        ? duration
        : fallbackDuration ?? Infinity;

    setIsSeekable(getIsSeekable());

    setChangeEvent({
      currentTime: clampedTime,
      duration: displayDuration,
      isPaused: paused,
    });

    // Safari: manually detect end when onended doesn't fire
    if (
      isSafari() &&
      effectiveDuration &&
      clampedTime >= effectiveDuration &&
      !paused &&
      currentAudioIndex >= 0
    ) {
      audio.pause();
      audio.currentTime = 0;
      isResetAfterEnd = true;
      scheduleAnimationFrame(() => {
        if (audio) {
          const resetDuration =
            isFinite(audio.duration) && audio.duration > 0
              ? audio.duration
              : fallbackDuration ?? Infinity;
          setChangeEvent({
            currentTime: 0,
            duration: resetDuration,
            isPaused: true,
          });
        }
      });
      isAutoPlay
        ? onAutoPlayNext(currentAudioIndex)
        : dispatch(setIsAudioPlaying(false));
    }
  };

  const playAudio = () => {
    ensureAudio();
    radioPlayer?.stop();
    dispatch(setCurrentPlayingVideo(null));
    audio.play();
    dispatch(setIsAudioPlaying(true));
  };

  const setEntryDurationFallback = (
    entry: ContentFeedEntrySoundpiece | PlaylistItemProps,
  ) => {
    fallbackDuration =
      'duration' in entry && typeof entry.duration === 'number'
        ? entry.duration
        : null;
  };

  const setEntrySource = (
    entry: ContentFeedEntrySoundpiece | PlaylistItemProps,
  ) => {
    if (!entry.audioUrl) return;

    baseAudioUrlForTcf = entry.audioUrl;
    let src = entry.audioUrl;

    try {
      src = addTCStringToUrlSync(src).toString();
    } catch (error) {
      console.warn('Error adding TC string to audio URL:', error);
    }

    const proxied = getProxiedAudioUrl(src);

    if (audio.src !== proxied) {
      audio.setAttribute('src', proxied);
      audio.load();
    }
  };

  const onPlay = (
    index: number,
    localEntry: ContentFeedEntrySoundpiece | PlaylistItemProps,
  ) => {
    ensureAudio();
    currentAudioIndex = index;
    dispatch(setCurrentAudio(localEntry));
    setEntryDurationFallback(localEntry);
    setEntrySource(localEntry);
    isResetAfterEnd = false;

    audio.onended = () => {
      if (isAutoPlay) {
        onAutoPlayNext(index);
      } else {
        audio.pause();
        dispatch(setIsAudioPlaying(false));
      }
    };

    audio.onloadedmetadata = () => {
      setIsSeekable(getIsSeekable());
    };

    audio.currentTime = 0;
    playAudio();
  };

  const onResume = () => {
    ensureAudio();
    const effectiveDuration = getEffectiveDuration(audio, fallbackDuration);

    // Safari (especially iOS): check if we're at or near the end (with small tolerance)
    // and reset to beginning. Also handle cases where currentTime might be stale.
    if (effectiveDuration) {
      const { currentTime } = audio;
      const tolerance = 0.1; // Small tolerance for Safari precision issues

      if (currentTime >= effectiveDuration - tolerance) {
        audio.currentTime = 0;
        // Mark that we've reset after reaching the end - disable skipping until play is clicked
        isResetAfterEnd = true;

        // Safari (especially iOS): manually update state immediately since timeupdate
        // might not fire reliably after resetting currentTime
        scheduleAnimationFrame(() => {
          updateChangeEvent();
        }, isSafari());
      }
    }

    // Re-enable skipping when user explicitly clicks play
    isResetAfterEnd = false;
    playAudio();
  };

  const onPause = () => {
    audio?.pause();
    dispatch(setIsAudioPlaying(false));
  };

  const onStop = useCallback(() => {
    if (!audio) return;
    audio.pause();
    audio.removeAttribute('src');
    baseAudioUrlForTcf = null;
    currentAudioIndex = -1;
    fallbackDuration = null;
    isResetAfterEnd = false;
    setIsSeekable(false);
    dispatch(setIsAudioPlaying(false));
  }, [dispatch]);

  const setCurrentTime = (newTime: number) => {
    if (!audio || audio.readyState < 2) return;
    audio.currentTime = clampTimeToPlayable(newTime, audio, fallbackDuration);

    // Manually update state to ensure progress bar reflects the change
    // This is especially important when audio is paused, as timeupdate events may not fire
    scheduleAnimationFrame(() => {
      updateChangeEvent();
    }, isSafari());
  };

  const skipAudio = (step: number) => {
    if (!audio) return;

    if (audio.readyState === 0) return; // no metadata yet

    // Prevent skipping when audio was reset to zero after reaching the end
    // until user explicitly clicks play again
    if (isResetAfterEnd) return;

    const { currentTime, duration } = audio;

    if (!isFinite(currentTime) || currentTime < 0) return;

    const newTime = currentTime + step;

    // If skipping backward would go below 0, reset to 0 and stop playback
    if (newTime < 0) {
      audio.pause();
      audio.currentTime = 0;
      isResetAfterEnd = true;
      dispatch(setIsAudioPlaying(false));

      // Update state to reflect the reset
      const displayDuration =
        isFinite(duration) && duration > 0
          ? duration
          : fallbackDuration ?? Infinity;

      if (isSafari()) {
        // Safari: Verify and correct currentTime if needed, then update state
        scheduleAnimationFrame(() => {
          if (audio) {
            // Ensure currentTime is actually 0 (Safari might not have processed it yet)
            if (audio.currentTime !== 0) {
              audio.currentTime = 0;
            }
            setChangeEvent({
              currentTime: 0,
              duration: displayDuration,
              isPaused: true,
            });
          }
        }, true);
      } else {
        // Other browsers: Update immediately
        setChangeEvent({
          currentTime: 0,
          duration: displayDuration,
          isPaused: true,
        });
      }

      return;
    }

    const target = clampTimeToPlayable(newTime, audio, fallbackDuration);

    try {
      audio.currentTime = target;

      // Manually update state to ensure progress bar reflects the change
      // This is especially important when audio is paused, as timeupdate events may not fire
      if (isSafari()) {
        // Safari: Verify and correct currentTime if needed, then update state
        scheduleAnimationFrame(() => {
          if (audio) {
            // Check if Safari actually processed the change (allow small tolerance for floating point)
            const timeDiff = Math.abs(audio.currentTime - target);

            if (timeDiff > 0.1) {
              // Safari didn't process it, try once more
              audio.currentTime = target;
            }
            updateChangeEvent();
          }
        });

        const effective =
          !isFinite(duration) && fallbackDuration && fallbackDuration > 0
            ? fallbackDuration
            : duration;

        if (isFinite(effective) && effective > 0) {
          scheduleAnimationFrame(() => {
            if (audio && audio.currentTime > effective) {
              audio.currentTime = effective;
              // Update state again after Safari correction
              updateChangeEvent();
            }
          });
        }
      } else {
        // Other browsers: Single RAF should be sufficient
        scheduleAnimationFrame(() => {
          updateChangeEvent();
        });
      }
    } catch (error) {
      console.error('[AudioPlayer] Error setting currentTime:', error);
    }
  };

  // lifecycle hooks

  useEffect(() => {
    ensureAudio();
    audio.addEventListener('timeupdate', updateChangeEvent);

    return () => audio.removeEventListener('timeupdate', updateChangeEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (audio) {
      audio.volume = isMuted ? 0 : volume / 100;
    }
  }, [volume, isMuted]);

  useEffect(() => {
    if (!currentAudio) {
      onStop();
      baseAudioUrlForTcf = null;
    }
  }, [currentAudio, onStop]);

  useEffect(() => {
    const handleTCFConsentChange = () => {
      if (!baseAudioUrlForTcf || !audio || !currentAudio) return;

      let audioUrl = baseAudioUrlForTcf;

      try {
        audioUrl = addTCStringToUrlSync(audioUrl).toString();
      } catch (error) {
        console.warn('Error updating TC string in audio URL:', error);

        return;
      }

      const proxiedAudioUrl = getProxiedAudioUrl(audioUrl);
      const wasPlaying = !audio.paused;
      const { currentTime } = audio;

      audio.setAttribute('src', proxiedAudioUrl);
      audio.currentTime = currentTime;
      audio.load();

      if (wasPlaying) {
        audio.play().catch((error) => {
          console.warn('Error resuming audio after consent change:', error);
        });
      }
    };

    document.addEventListener(
      TCF_CONSENT_CHANGED_EVENT_TYPE,
      handleTCFConsentChange,
    );

    return () => {
      document.removeEventListener(
        TCF_CONSENT_CHANGED_EVENT_TYPE,
        handleTCFConsentChange,
      );
    };
  }, [currentAudio]);

  return {
    onPlay,
    onPause,
    onStop,
    onResume,
    setIsAutoPlay,
    changeEvent,
    skipAudio,
    setCurrentTime,
    isSeekable,
    addToAutoPlaylist: addToLocalAutoPlaylist,
  };
};

export default useAudioPlayer;
